/**
 * See hdm-order-status.json for API details.
 * integration/specs/custom/hdm-order-status.json
 */
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { ORDER_DELIVERY_DETAILS_KEY } from '@/data/constants/hdmDataKey';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getSettings, useSettings } from '@/data/Settings';
import { Cache } from '@/data/types/Cache';
import { getUser } from '@/data/User-Server';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getRequestId } from '@/data/utils/getRequestId';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { traceWithId } from '@/data/utils/loggerUtil';
import { customDeliveryDetails } from 'integration/generated/custom';

import { OrderDeliveryDetailsResponse } from 'integration/generated/custom/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize } from 'swr';

const getOrderDeliveryDetailsSWRKey = (storeId: string, orderId: string) => ({
	storeId,
	orderId,
});

type DeliveryDetailsQueryType = {
	responseFormat: 'json' | 'xml';
	checkoutPage?: boolean;
	confirmationPage?: boolean;
	orderDetailsPage?: boolean;
	typSort?: boolean;
	itemDeliveryType?: 'Fleet' | 'Parcel';
};

export const orderDeliveryDetailsFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		orderId: string,
		query: DeliveryDetailsQueryType,
		params: RequestParams
	): Promise<OrderDeliveryDetailsResponse> => {
		const res = await customDeliveryDetails(pub).deliveryDetails(storeId, orderId, query, params);
		return res;
	};

/**
 * Server-side function to fetch the order delivery details.
 * This function is intended to be used in `getServerSideProps` and leverages the server-side cache.
 *
 * @param cache The server-side cache instance.
 * @param context The `GetServerSidePropsContext` object.
 * @returns The order delivery details or null if an error occurs.
 */
export const getOrderDeliveryDetails = async (
	cache: Cache,
	context: GetServerSidePropsContext,
	orderId: string,
	query: DeliveryDetailsQueryType,
	params: RequestParams
): Promise<OrderDeliveryDetailsResponse | null> => {
	const requestId = getRequestId(context);
	traceWithId(requestId, `getOrderDeliveryDetails: start`);
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { storeId } = getServerSideCommon(settings, context);

	if (!storeId) {
		traceWithId(requestId, `getOrderDeliveryDetails: end (missing storeId)`);
		return null;
	}
	const props = getOrderDeliveryDetailsSWRKey(storeId, orderId);
	const key = unstable_serialize([shrink(props), ORDER_DELIVERY_DETAILS_KEY]);
	const cacheScope = getServerCacheScope(context, user.context);

	const value = await cache.get(key);
	if (value) {
		traceWithId(requestId, `getOrderDeliveryDetails: end (from cache)`);
		return value;
	}
	try {
		const response = await orderDeliveryDetailsFetcher(false)(storeId, orderId, query, params);

		if (response) {
			cache.set(key, response, cacheScope);
			traceWithId(requestId, `getOrderDeliveryDetails: end (filled cache)`);
		}
		return response;
	} catch (error) {
		console.error(`Failed to fetch order delivery details:`, error);
		traceWithId(requestId, `getOrderDeliveryDetails: end (with error)`);
		return null;
	}
};

/**
 * Client-side React hook to fetch specified order status and delivery details using SWR.
 *
 * @returns An object containing the order delivery details data, loading state, and error state.
 */
export const useOrderStatus = (orderId: string, query: DeliveryDetailsQueryType) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const key = getOrderDeliveryDetailsSWRKey(storeId, orderId);

	const {
		data: orderDeliveryDetails,
		error,
		isLoading,
	} = useSWR<OrderDeliveryDetailsResponse>(
		storeId ? [shrink(key), ORDER_DELIVERY_DETAILS_KEY] : null,
		() => orderDeliveryDetailsFetcher(true)(storeId, orderId, query, params),
		{ use: [laggyMiddleWare] }
	);

	return {
		orderDeliveryDetails,
		error,
		isLoading,
	};
};
export const orderStatus = (
	storeId: string,
	orderId: string,
	query: DeliveryDetailsQueryType,
	params: RequestParams = {}
) => orderDeliveryDetailsFetcher(true)(storeId, orderId, query, params);
